/* VASHP product page — gallery, variant picker, add to cart, sticky bar, drawers, reveals */
(() => {
  if (window.VashpPDP) return;
  window.VashpPDP = true;

  const moneyFormat = (window.theme && window.theme.settings && window.theme.settings.money_format) || '₹{{amount}}';
  const formatMoney = (cents) => {
    const value = Number(cents) / 100;
    const withDelims = (n, decimals, thousands = ',', decimal = '.') => {
      const parts = n.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    };
    return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return withDelims(value, 0);
        case 'amount_with_comma_separator': return withDelims(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelims(value, 0, '.', ',');
        default: return withDelims(value, 2);
      }
    }).replace(/<[^>]*>/g, '');
  };

  const lock = (on) => document.documentElement.classList.toggle('vp-lock', on);

  /* ---------- Reveal on scroll (all vp sections) ---------- */
  const revealObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('is-in'); revealObserver.unobserve(e.target); }
      }), { rootMargin: '0px 0px -8% 0px' })
    : null;
  const observeReveals = (root = document) => root.querySelectorAll('.vp-reveal:not(.is-in)').forEach((el) => {
    if (revealObserver) revealObserver.observe(el); else el.classList.add('is-in');
  });
  document.addEventListener('DOMContentLoaded', () => observeReveals());
  document.addEventListener('shopify:section:load', (e) => observeReveals(e.target));
  if (document.readyState !== 'loading') observeReveals();

  /* ---------- Main product element ---------- */
  class VpProduct extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-vp-product-json]');
      if (!json) return;
      this.product = JSON.parse(json.textContent);
      this.cfg = JSON.parse(this.querySelector('[data-vp-config]').textContent);
      this.form = this.querySelector('form[data-type="add-to-cart-form"]');
      this.idInput = this.form ? this.form.querySelector('input[name="id"]') : null;
      this.stage = this.querySelector('[data-vp-stage]');
      this.slides = this.stage ? [...this.stage.querySelectorAll('[data-vp-slide]')] : [];
      this.status = this.querySelector('[data-vp-status]');
      this.sticky = this.querySelector('[data-vp-sticky]');
      this.index = 0;

      const initial = this.cfg.initialVariant ? this.product.variants.find((v) => v.id === this.cfg.initialVariant) : null;
      this.selected = this.product.options.map((_, i) => (initial ? initial.options[i] : null));
      this.product.options.forEach((_, i) => {
        const values = [...new Set(this.product.variants.map((v) => v.options[i]))];
        if (values.length === 1) this.selected[i] = values[0];
      });

      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('keydown', (e) => this.onKeydown(e));
      if (this.form) this.form.addEventListener('submit', (e) => { e.preventDefault(); this.addToCart(); });
      if (this.stage) {
        let t;
        this.stage.addEventListener('scroll', () => { clearTimeout(t); t = setTimeout(() => this.syncIndex(), 60); }, { passive: true });
      }
      this.onDocKey = (e) => {
        if (e.key === 'Escape') { this.closeDrawers(); this.closeLightbox(); }
        if (this.lbOpen && ['ArrowLeft', 'ArrowRight'].includes(e.key)) { e.preventDefault(); this.lbStep(e.key === 'ArrowRight' ? 1 : -1); }
      };
      document.addEventListener('keydown', this.onDocKey);
      this.initSticky();
      this.update(false);
      observeReveals(this);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onDocKey);
      if (this.stickyObserver) this.stickyObserver.disconnect();
      lock(false);
    }

    /* ----- Events ----- */
    onClick(e) {
      const opt = e.target.closest('[data-vp-value]');
      if (opt) {
        const i = Number(opt.dataset.vpOption);
        this.selected[i] = opt.dataset.vpValue;
        this.update(true);
        return;
      }
      const go = e.target.closest('[data-vp-go]');
      if (go) return this.goTo(Number(go.dataset.vpGo));
      if (e.target.closest('[data-vp-prev]')) return this.goTo(this.index - 1);
      if (e.target.closest('[data-vp-next]')) return this.goTo(this.index + 1);
      const zoom = e.target.closest('[data-vp-zoom]');
      if (zoom) return this.openLightbox(Number(zoom.dataset.vpZoom));
      const drawer = e.target.closest('[data-vp-open-drawer]');
      if (drawer) { e.preventDefault(); return this.openDrawer(drawer.dataset.vpOpenDrawer); }
      if (e.target.closest('[data-vp-close-drawer]')) return this.closeDrawers();
      const unit = e.target.closest('[data-vp-unit]');
      if (unit) {
        const chart = unit.closest('[data-vp-sizechart]');
        chart.dataset.unit = unit.dataset.vpUnit;
        chart.querySelectorAll('[data-vp-unit]').forEach((u) => u.setAttribute('aria-pressed', String(u === unit)));
        return;
      }
      if (e.target.closest('[data-vp-lb-close]')) return this.closeLightbox();
      const lbGo = e.target.closest('[data-vp-lb-go]');
      if (lbGo) return this.lbGoTo(Number(lbGo.dataset.vpLbGo));
      if (e.target.closest('[data-vp-lb-prev]')) return this.lbStep(-1);
      if (e.target.closest('[data-vp-lb-next]')) return this.lbStep(1);
      if (e.target.closest('[data-vp-sticky-add]')) {
        if (this.missingOption() !== -1) {
          this.scrollIntoPicker();
          this.flagMissing();
          return;
        }
        return this.addToCart();
      }
      const qty = e.target.closest('[data-vp-qty]');
      if (qty) {
        const input = this.querySelector('[data-vp-qty-input]');
        input.value = Math.max(1, (parseInt(input.value, 10) || 1) + Number(qty.dataset.vpQty));
        return;
      }
      const share = e.target.closest('[data-vp-share]');
      if (share) {
        const data = { title: this.product.title, url: location.href };
        if (navigator.share) navigator.share(data).catch(() => {});
        else if (navigator.clipboard) navigator.clipboard.writeText(location.href).then(() => { share.textContent = share.dataset.copied || 'Link copied'; });
      }
    }

    onKeydown(e) {
      if (e.target.closest('[data-vp-stage]') && ['ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
        this.goTo(this.index + (e.key === 'ArrowRight' ? 1 : -1));
      }
      const radio = e.target.closest('[data-vp-value]');
      if (radio && ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
        const group = [...radio.parentElement.querySelectorAll('[data-vp-value]')];
        const dir = ['ArrowRight', 'ArrowDown'].includes(e.key) ? 1 : -1;
        const next = group[(group.indexOf(radio) + dir + group.length) % group.length];
        next.focus();
        next.click();
        e.preventDefault();
      }
    }

    /* ----- Variants ----- */
    currentVariant() {
      if (this.selected.some((v) => v === null)) return null;
      return this.product.variants.find((v) => v.options.every((o, i) => o === this.selected[i])) || null;
    }

    missingOption() { return this.selected.findIndex((v) => v === null); }

    update(userAction) {
      const variant = this.currentVariant();
      const missing = this.missingOption();
      this.classList.toggle('is-awaiting-choice', missing !== -1);

      // Option buttons: selection + availability for the current partial choice
      this.querySelectorAll('[data-vp-option-group]').forEach((group) => {
        const i = Number(group.dataset.vpOptionGroup);
        group.closest('.vp-option').classList.toggle('is-chosen', this.selected[i] !== null);
        const label = group.closest('.vp-option').querySelector('[data-vp-selected-label]');
        if (label) label.textContent = this.selected[i] ? '— ' + this.selected[i] : '';
        group.querySelectorAll('[data-vp-value]').forEach((btn) => {
          const value = btn.dataset.vpValue;
          const available = this.product.variants.some((v) => v.available && v.options[i] === value &&
            v.options.every((o, j) => j === i || this.selected[j] === null || this.selected[j] === o));
          btn.classList.toggle('is-unavailable', !available);
          btn.setAttribute('aria-checked', String(this.selected[i] === value));
          btn.tabIndex = this.selected[i] === value || (this.selected[i] === null && btn === group.querySelector('[data-vp-value]')) ? 0 : -1;
        });
      });
      if (missing === -1) this.querySelector('[data-vp-options]')?.classList.remove('is-invalid');

      // Hidden input + button state
      const addLabel = this.cfg.strings.add;
      let buttonText = addLabel;
      let disabled = false;
      if (missing !== -1) {
        buttonText = this.cfg.strings.select.replace('[option]', this.product.options[missing].toLowerCase());
      } else if (!variant) {
        buttonText = this.cfg.strings.unavailable; disabled = true;
      } else if (!variant.available) {
        buttonText = this.cfg.strings.soldOut; disabled = true;
      }
      if (this.idInput) {
        this.idInput.value = variant ? variant.id : '';
        this.idInput.disabled = !variant || !variant.available;
      }
      this.querySelectorAll('[data-vp-add-label]').forEach((el) => { el.textContent = buttonText; });
      this.querySelectorAll('[data-vp-add]').forEach((btn) => { btn.disabled = disabled; });

      // Price
      const priceVariant = variant || this.product.variants.find((v) => v.available) || this.product.variants[0];
      this.renderPrice(priceVariant, !variant && this.product.price_varies);

      // Stock line
      const stock = this.querySelector('[data-vp-stock]');
      if (stock) {
        stock.hidden = !variant;
        if (variant) {
          const q = variant.inventory_quantity;
          const tracked = variant.inventory_management === 'shopify' && variant.inventory_policy !== 'continue';
          stock.className = 'vp-stock';
          if (!variant.available) { stock.classList.add('is-out'); stock.textContent = this.cfg.strings.soldOut; }
          else if (tracked && q > 0 && q <= this.cfg.lowStock) { stock.classList.add('is-low'); stock.textContent = this.cfg.strings.low.replace('[count]', q); }
          else stock.textContent = this.cfg.strings.inStock;
        }
      }

      // Sticky meta
      if (this.sticky) {
        const meta = this.sticky.querySelector('[data-vp-sticky-meta]');
        if (meta) meta.textContent = [variant ? variant.title !== 'Default Title' ? variant.title : '' : '', formatMoney(priceVariant.price)].filter(Boolean).join(' · ');
      }

      if (userAction) {
        if (variant) {
          const url = new URL(location.href);
          url.searchParams.set('variant', variant.id);
          history.replaceState(history.state, '', url);
          if (variant.featured_media) {
            const idx = this.slides.findIndex((s) => Number(s.dataset.mediaId) === variant.featured_media.id);
            if (idx > -1) this.goTo(idx);
          }
        }
        if (this.status) { this.status.textContent = ''; this.status.classList.remove('is-error'); }
        this.dispatchEvent(new CustomEvent('vashp:variant-change', { bubbles: true, detail: { variant } }));
      }
    }

    renderPrice(variant, from) {
      const now = this.querySelector('[data-vp-price-now]');
      const was = this.querySelector('[data-vp-price-was]');
      const save = this.querySelector('[data-vp-price-save]');
      if (!now) return;
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      now.textContent = (from ? this.cfg.strings.from + ' ' : '') + formatMoney(variant.price);
      now.classList.toggle('is-sale', !!onSale);
      if (was) { was.hidden = !onSale; if (onSale) was.textContent = formatMoney(variant.compare_at_price); }
      if (save) {
        save.hidden = !onSale;
        if (onSale) {
          const pct = Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price);
          save.textContent = this.cfg.strings.save.replace('[percent]', pct).replace('[amount]', formatMoney(variant.compare_at_price - variant.price));
        }
      }
    }

    flagMissing() {
      const opts = this.querySelector('[data-vp-options]');
      if (!opts) return;
      opts.classList.remove('is-invalid');
      void opts.offsetWidth;
      opts.classList.add('is-invalid');
      const missing = this.missingOption();
      this.setStatus(this.cfg.strings.pleaseSelect.replace('[option]', this.product.options[missing].toLowerCase()), true);
      const first = opts.querySelector(`[data-vp-option-group="${missing}"] [data-vp-value]`);
      if (first) first.focus({ preventScroll: true });
    }

    scrollIntoPicker() {
      const opts = this.querySelector('[data-vp-options]');
      if (opts) window.scrollTo({ top: opts.getBoundingClientRect().top + scrollY - 120, behavior: 'smooth' });
    }

    setStatus(text, error = false) {
      if (!this.status) return;
      this.status.textContent = text;
      this.status.classList.toggle('is-error', error);
    }

    /* ----- Cart ----- */
    async addToCart() {
      if (this.missingOption() !== -1) return this.flagMissing();
      const variant = this.currentVariant();
      if (!variant || !variant.available) return;
      const buttons = [...this.querySelectorAll('[data-vp-add]')];
      buttons.forEach((b) => b.classList.add('is-loading'));
      const cartDrawer = document.querySelector('cart-drawer');
      const body = new FormData(this.form);
      body.set('id', variant.id);
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        body.append('sections', cartDrawer.getSectionsToRender().map((s) => s.section));
        body.append('sections_url', location.pathname);
      }
      try {
        const res = await fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`, {
          method: 'POST', body, headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' }
        });
        const data = await res.json();
        if (data.status) {
          this.setStatus(data.description || data.message || this.cfg.strings.error, true);
          return;
        }
        const label = variant.title !== 'Default Title' ? ` · ${variant.title}` : '';
        this.setStatus(this.cfg.strings.added + label);
        this.querySelectorAll('[data-vp-add-label]').forEach((el) => { el.textContent = this.cfg.strings.addedButton; });
        setTimeout(() => this.update(false), 2200);
        if (cartDrawer && typeof cartDrawer.renderContents === 'function' && data.sections) {
          cartDrawer.renderContents(data, this.cfg.openDrawer);
        } else if (this.cfg.openDrawer) {
          location.href = (window.routes && window.routes.cart_url) || '/cart';
        }
        document.dispatchEvent(new CustomEvent('vashp:cart:add', { detail: { item: data } }));
      } catch (err) {
        this.setStatus(this.cfg.strings.error, true);
      } finally {
        buttons.forEach((b) => b.classList.remove('is-loading'));
      }
    }

    /* ----- Gallery ----- */
    goTo(i) {
      if (!this.slides.length) return;
      i = Math.max(0, Math.min(this.slides.length - 1, i));
      this.stage.scrollTo({ left: this.slides[i].offsetLeft, behavior: 'smooth' });
      this.setIndex(i);
    }

    syncIndex() {
      if (!this.slides.length) return;
      const i = Math.round(this.stage.scrollLeft / this.stage.clientWidth);
      if (i !== this.index) this.setIndex(i);
    }

    setIndex(i) {
      this.index = i;
      this.querySelectorAll('[data-vp-go]').forEach((b) => b.setAttribute('aria-current', String(Number(b.dataset.vpGo) === i)));
      const counter = this.querySelector('[data-vp-counter]');
      if (counter) counter.textContent = `${i + 1} / ${this.slides.length}`;
      const prev = this.querySelector('[data-vp-prev]');
      const next = this.querySelector('[data-vp-next]');
      if (prev) prev.disabled = i === 0;
      if (next) next.disabled = i === this.slides.length - 1;
      this.slides.forEach((s, j) => {
        s.setAttribute('aria-hidden', String(j !== i));
        const vid = s.querySelector('video');
        if (vid && j !== i) vid.pause();
      });
    }

    openLightbox(i) {
      const lb = this.querySelector('[data-vp-lightbox]');
      if (!lb) return;
      lb.querySelectorAll('img[data-src]').forEach((img) => { img.src = img.dataset.src; img.removeAttribute('data-src'); });
      if (!lb.dataset.ready) this.initLightbox(lb);
      this.lbOpen = true;
      this.lbLast = document.activeElement;
      lb.setAttribute('aria-hidden', 'false');
      lock(true);
      const track = lb.querySelector('[data-vp-lb-track]');
      track.style.scrollBehavior = 'auto';
      track.scrollLeft = track.clientWidth * i;
      track.style.scrollBehavior = '';
      this.lbSync(i);
      requestAnimationFrame(() => lb.classList.add('is-open'));
      lb.querySelector('[data-vp-lb-close]').focus({ preventScroll: true });
    }

    initLightbox(lb) {
      lb.dataset.ready = '1';
      const track = lb.querySelector('[data-vp-lb-track]');
      const SCALE = 2.6;
      let t;
      track.addEventListener('scroll', () => {
        clearTimeout(t);
        t = setTimeout(() => this.lbSync(Math.round(track.scrollLeft / track.clientWidth)), 60);
      }, { passive: true });

      // Zoom into the exact point pressed; mouse movement or drag explores the area
      let drag = null;
      const setOrigin = (slide, x, y) => {
        slide.dataset.ox = Math.min(100, Math.max(0, x));
        slide.dataset.oy = Math.min(100, Math.max(0, y));
        slide.style.setProperty('--vp-ox', `${slide.dataset.ox}%`);
        slide.style.setProperty('--vp-oy', `${slide.dataset.oy}%`);
      };
      const frameOf = (slide) => slide.querySelector('[data-vp-lb-frame]') || slide;
      const pointFor = (slide, e) => {
        const r = frameOf(slide).getBoundingClientRect();
        return [((e.clientX - r.left) / r.width) * 100, ((e.clientY - r.top) / r.height) * 100];
      };
      track.addEventListener('pointerdown', (e) => {
        const slide = e.target.closest('[data-vp-lb-slide]');
        if (!slide || e.button > 0) return;
        drag = { slide, x: e.clientX, y: e.clientY, ox: Number(slide.dataset.ox || 50), oy: Number(slide.dataset.oy || 50), moved: false };
        if (slide.classList.contains('is-zoomed') && e.pointerType !== 'mouse') {
          try { slide.setPointerCapture(e.pointerId); } catch (err) { /* pointer already released */ }
          slide.classList.add('is-dragging');
        }
      });
      track.addEventListener('pointermove', (e) => {
        const slide = e.target.closest('[data-vp-lb-slide]');
        if (e.pointerType === 'mouse') {
          if (drag) drag.moved = drag.moved || Math.hypot(e.clientX - drag.x, e.clientY - drag.y) > 6;
          if (slide && slide.classList.contains('is-zoomed')) setOrigin(slide, ...pointFor(slide, e));
          return;
        }
        if (!drag || !drag.slide.classList.contains('is-zoomed')) return;
        const dx = e.clientX - drag.x;
        const dy = e.clientY - drag.y;
        if (Math.hypot(dx, dy) > 6) drag.moved = true;
        const r = frameOf(drag.slide).getBoundingClientRect();
        setOrigin(drag.slide, drag.ox - (dx / r.width) * 100 / (SCALE - 1), drag.oy - (dy / r.height) * 100 / (SCALE - 1));
      });
      const end = (e) => {
        if (!drag) return;
        const { slide, moved } = drag;
        drag = null;
        slide.classList.remove('is-dragging');
        if (moved || e.type === 'pointercancel') return;
        if (!e.target.closest('[data-vp-lb-img]')) return;
        if (slide.classList.contains('is-zoomed')) {
          slide.classList.remove('is-zoomed');
          track.classList.remove('is-locked');
        } else {
          setOrigin(slide, ...pointFor(slide, e));
          slide.classList.add('is-zoomed');
          track.classList.add('is-locked');
        }
      };
      track.addEventListener('pointerup', end);
      track.addEventListener('pointercancel', end);
      lb.addEventListener('click', (e) => { if (e.target === lb || e.target === track) this.closeLightbox(); });
    }

    lbSync(i) {
      const lb = this.querySelector('[data-vp-lightbox]');
      if (!lb) return;
      this.lbIndex = i;
      const cur = lb.querySelector('[data-vp-lb-current]');
      if (cur) cur.textContent = String(i + 1).padStart(2, '0');
      lb.querySelectorAll('[data-vp-lb-slide]').forEach((s, j) => {
        s.classList.toggle('is-current', j === i);
        if (j !== i) s.classList.remove('is-zoomed');
      });
      lb.querySelector('[data-vp-lb-track]').classList.toggle('is-locked', !!lb.querySelector('.is-zoomed'));
      lb.querySelectorAll('[data-vp-lb-go]').forEach((b, j) => {
        b.classList.toggle('is-active', j === i);
        if (j === i) b.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
      });
      const prev = lb.querySelector('[data-vp-lb-prev]');
      const next = lb.querySelector('[data-vp-lb-next]');
      const last = lb.querySelectorAll('[data-vp-lb-slide]').length - 1;
      if (prev) prev.disabled = i === 0;
      if (next) next.disabled = i === last;
    }

    lbGoTo(i) {
      const track = this.querySelector('[data-vp-lb-track]');
      const last = track.children.length - 1;
      const n = Math.max(0, Math.min(last, i));
      track.querySelectorAll('.is-zoomed').forEach((s) => s.classList.remove('is-zoomed'));
      track.classList.remove('is-locked');
      track.scrollTo({ left: n * track.clientWidth, behavior: 'smooth' });
      this.lbSync(n);
    }

    lbStep(dir) {
      this.lbGoTo((this.lbIndex || 0) + dir);
    }

    closeLightbox() {
      const lb = this.querySelector('[data-vp-lightbox]');
      if (!lb || !this.lbOpen) return;
      this.lbOpen = false;
      lb.classList.remove('is-open');
      lb.setAttribute('aria-hidden', 'true');
      lb.querySelectorAll('.is-zoomed').forEach((s) => s.classList.remove('is-zoomed'));
      lb.querySelector('[data-vp-lb-track]').classList.remove('is-locked');
      lock(false);
      if (this.lbLast) this.lbLast.focus({ preventScroll: true });
    }

    /* ----- Drawers ----- */
    openDrawer(id) {
      const d = this.querySelector(`[data-vp-drawer="${id}"]`);
      if (!d) return;
      this.lastFocus = document.activeElement;
      const picked = (this.selected || []).filter(Boolean).map((v) => String(v).toLowerCase());
      d.querySelectorAll('[data-vp-size-row]').forEach((r) => r.classList.toggle('is-selected', picked.includes(r.dataset.vpSizeRow.toLowerCase())));
      d.classList.add('is-open');
      d.setAttribute('aria-hidden', 'false');
      lock(true);
      const close = d.querySelector('[data-vp-close-drawer].vp-close');
      if (close) close.focus();
    }

    closeDrawers() {
      const open = this.querySelectorAll('[data-vp-drawer].is-open');
      if (!open.length) return;
      open.forEach((d) => { d.classList.remove('is-open'); d.setAttribute('aria-hidden', 'true'); });
      lock(false);
      if (this.lastFocus) this.lastFocus.focus();
    }

    /* ----- Sticky bar ----- */
    initSticky() {
      const target = this.querySelector('[data-vp-buttons]');
      if (!this.sticky || !target || !('IntersectionObserver' in window)) return;
      this.stickyObserver = new IntersectionObserver(([entry]) => {
        const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.sticky.classList.toggle('is-visible', passed);
        this.sticky.setAttribute('aria-hidden', String(!passed));
      });
      this.stickyObserver.observe(target);
    }
  }
  if (!customElements.get('vp-product')) customElements.define('vp-product', VpProduct);

  /* ---------- Recommendations (More from / pairings fallback) ---------- */
  class VpRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this.loaded) return;
      const load = () => {
        this.loaded = true;
        fetch(this.dataset.url)
          .then((r) => r.text())
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const fresh = doc.querySelector(`vp-recommendations[data-key="${this.dataset.key}"]`);
            if (fresh && fresh.querySelector('[data-vp-rec-item]')) {
              this.innerHTML = fresh.innerHTML;
              this.hidden = false;
              const section = this.closest('[data-vp-rec-section]');
              if (section) section.hidden = false;
              observeReveals(this);
            }
          })
          .catch(() => {});
      };
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { io.disconnect(); load(); } }, { rootMargin: '0px 0px 600px 0px' });
        io.observe(this.closest('.shopify-section') || this.parentElement || this);
      } else load();
    }
  }
  if (!customElements.get('vp-recommendations')) customElements.define('vp-recommendations', VpRecommendations);
})();
